import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Trophy, Search, Star, Loader2 } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import ConsultationDialog from "@/components/ConsultationDialog";
import { getInitials } from "@/lib/image";
import useSEO from "@/lib/useSEO";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "all", label: "All Achievers" },
  { id: "CMA USA", label: "CMA USA" },
  { id: "ACCA", label: "ACCA" },
];

function isHighScorer(t) {
  if (t.course === "ACCA") return false;
  return Number(t.mark) >= 800;
}

export default function Toppers() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Toppers & Pass-Outs | Educators Institute of Management",
    description:
      "Real students, real results. Meet the ACCA and CMA USA achievers from Educators Institute of Management — Thiruvananthapuram, Kerala.",
    canonical: typeof window !== "undefined" ? window.location.href : undefined,
    image: SITE.media.teamWithLogo,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/toppers`);
        if (!cancel) setList((data || []).filter((t) => t.featured));
      } catch (err) {
        // graceful fallback: empty list
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const counts = useMemo(() => {
    const cma = list.filter((t) => t.course === "CMA USA");
    const acca = list.filter((t) => t.course === "ACCA");
    const high = cma.filter(isHighScorer);
    return { cma: cma.length, acca: acca.length, high: high.length };
  }, [list]);

  const visible = useMemo(() => {
    let arr = list;
    if (tab !== "all") arr = arr.filter((t) => t.course === tab);
    const q = query.trim().toLowerCase();
    if (q) arr = arr.filter((t) => t.name.toLowerCase().includes(q));
    return arr;
  }, [tab, query, list]);

  return (
    <div className="App font-inter text-slate-800">
      <Header onConsult={() => setOpen(true)} />

      <section className="relative bg-[#002570] text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.18]" style={{ backgroundImage: `url(${SITE.media.classroomA})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002570] via-[#002570]/95 to-[#001a52]/95" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 font-inter text-sm text-[#73c3ff] hover:text-white mb-5" data-testid="toppers-back-link">
            ← Back to home
          </Link>
          <div className="inline-flex items-center gap-2 bg-[#73c3ff]/20 text-[#73c3ff] px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase">
            <Trophy className="w-3.5 h-3.5" />
            Pass-Outs Hall of Fame
          </div>
          <h1 className="mt-5 font-rubik text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-balance max-w-4xl mx-auto">
            Real students. <span className="text-[#73c3ff]">Real results.</span>
          </h1>
          <p className="mt-5 font-inter text-base md:text-lg text-white/85 max-w-2xl mx-auto">
            These are pass-outs from Educators Institute of Management — every name below cleared their exam under our mentorship.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl mx-auto">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4">
              <p className="font-rubik text-3xl font-extrabold text-[#73c3ff]">{counts.cma}</p>
              <p className="font-inter text-xs uppercase tracking-wider text-white/70 mt-1">CMA USA</p>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4">
              <p className="font-rubik text-3xl font-extrabold text-[#73c3ff]">{counts.acca}</p>
              <p className="font-inter text-xs uppercase tracking-wider text-white/70 mt-1">ACCA</p>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4">
              <p className="font-rubik text-3xl font-extrabold text-[#73c3ff]">{counts.high}</p>
              <p className="font-inter text-xs uppercase tracking-wider text-white/70 mt-1">800+ scorers</p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-slate-100 sticky top-[72px] md:top-[84px] z-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex flex-col md:flex-row md:items-center gap-3 justify-between">
          <div className="flex flex-wrap gap-2" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={`toppers-tab-${t.id === "all" ? "all" : t.id === "CMA USA" ? "cma" : "acca"}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2 rounded-full text-sm font-inter font-medium transition-all ${
                  tab === t.id ? "bg-[#002570] text-white shadow-md" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="relative md:w-72">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="toppers-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name…"
              className="w-full bg-[#f8fafc] rounded-full border border-slate-200 pl-11 pr-4 py-2.5 font-inter text-sm focus:outline-none focus:ring-4 focus:ring-[#73c3ff]/30 focus:border-[#73c3ff] transition"
            />
          </div>
        </div>
      </section>

      <section className="bg-[#f8fafc] py-16 md:py-20 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading achievers…</div>
          ) : visible.length === 0 ? (
            <p className="text-center font-inter text-slate-500 py-20">No achievers match your search.</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="toppers-grid">
              {visible.map((t, i) => {
                const isACCA = t.course === "ACCA";
                const big = isHighScorer(t);
                return (
                  <div
                    key={t.id}
                    data-testid={`topper-card-${i}`}
                    className={`relative bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 ${big ? "ring-2 ring-[#73c3ff]/40" : ""}`}
                  >
                    {big && (
                      <span className="absolute -top-3 right-5 inline-flex items-center gap-1 bg-[#73c3ff] text-[#002570] text-[11px] font-inter font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        <Star className="w-3 h-3 fill-[#002570]" /> 800+ Scorer
                      </span>
                    )}
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-2xl overflow-hidden text-white flex items-center justify-center font-rubik text-xl font-bold shrink-0 ${big ? "bg-gradient-to-br from-[#73c3ff] to-[#002570]" : "bg-gradient-to-br from-[#002570] to-[#001a52]"}`}>
                        {t.photo ? <img src={t.photo} alt={t.name} className="w-full h-full object-cover" /> : <span>{getInitials(t.name)}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-rubik text-lg font-bold text-[#0f172a] truncate">{t.name}</p>
                        <span className="inline-block mt-1 bg-[#73c3ff]/15 text-[#002570] text-[11px] font-inter font-semibold px-2.5 py-0.5 rounded-full">{t.course}</span>
                      </div>
                    </div>

                    {isACCA ? (
                      <ul className="mt-5 space-y-2">
                        {(t.scores || []).map((s, j) => (
                          <li key={j} className="flex items-center justify-between bg-[#f8fafc] rounded-xl px-3 py-2 border border-slate-100">
                            <span className="font-inter text-sm font-medium text-slate-700">{s.paper}</span>
                            <span className="font-rubik text-sm font-bold text-[#002570]">{s.mark}<span className="text-slate-400 font-normal">/100</span></span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mt-5 bg-[#f8fafc] rounded-xl p-4 border border-slate-100">
                        <p className="font-inter text-xs uppercase tracking-wider text-slate-500">{t.part}</p>
                        <p className="mt-1 flex items-baseline gap-1.5">
                          <span className="font-rubik text-3xl font-extrabold text-[#002570]">{t.mark}</span>
                          <span className="font-inter text-sm text-slate-500">/ {t.max_mark}</span>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-white py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
          <h2 className="font-rubik text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0f172a] text-balance">
            The next name on this list <span className="text-[#002570]">could be yours.</span>
          </h2>
          <p className="mt-4 font-inter text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
            Talk to our counsellor for free. We'll map your path from where you are today, all the way to your designation.
          </p>
          <button
            onClick={() => setOpen(true)}
            data-testid="toppers-cta-btn"
            className="mt-8 inline-flex items-center gap-2 bg-[#002570] text-white hover:bg-[#001a52] rounded-full px-7 py-4 font-inter font-medium transition-all hover:shadow-xl"
          >
            Book Free Career Consultation <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
      <ConsultationDialog open={open} onOpenChange={setOpen} source="toppers-page" />
    </div>
  );
}
